/**
 * Enhanced TargetGuestList Component
 * 
 * Displays a list of target podcast guests with filtering, sorting, and virtualized rendering
 * for improved performance with large datasets.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useTargetGuests } from './useTargetGuests';
import { TargetGuest } from '@/types/models/schema';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  UserPlus, 
  Search, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp, 
  Filter, 
  X, 
  UserCheck 
} from 'lucide-react';
import ConvertToGuest from './ConvertToGuest';
import usePerformanceMeasure from '@/shared/utils/performance';
import { ApiResponse } from '@/types/api';

// Status color mapping
const STATUS_COLORS = {
  pending: 'bg-gray-200 text-gray-800',
  contacted: 'bg-blue-200 text-blue-800',
  interested: 'bg-green-200 text-green-800',
  scheduled: 'bg-purple-200 text-purple-800',
  converted: 'bg-indigo-200 text-indigo-800',
  declined: 'bg-red-200 text-red-800'
};

// Priority level mapping
const PRIORITY_LABELS = {
  '1': 'High',
  '2': 'High-Medium',
  '3': 'Medium',
  '4': 'Medium-Low',
  '5': 'Low'
};

/**
 * Interface for filter state
 */
interface FilterState {
  search: string;
  status: string;
  priority: string;
}

/**
 * Interface for sort state
 */
interface SortState {
  field: keyof TargetGuest | '';
  direction: 'asc' | 'desc';
}

/**
 * Props for the TargetGuestRow component
 */
interface TargetGuestRowProps {
  targetGuest: TargetGuest;
  style: React.CSSProperties;
  onConvertClick: (id: string) => void;
}

/**
 * Memoized row component for virtualized list
 */
const TargetGuestRow = React.memo(({ targetGuest, style, onConvertClick }: TargetGuestRowProps) => {
  const statusClass = STATUS_COLORS[targetGuest.status as keyof typeof STATUS_COLORS] || STATUS_COLORS.pending;
  const priorityLabel = targetGuest.priority ? 
    PRIORITY_LABELS[targetGuest.priority.toString() as keyof typeof PRIORITY_LABELS] : 
    'Medium';
  
  return (
    <div style={style} className="px-4 py-2 border-b border-gray-200 hover:bg-gray-50">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex-1">
          <Link 
            to={`/target-guests/${targetGuest.id}`} 
            className="text-lg font-medium text-primary-600 hover:text-primary-800 transition-colors"
          >
            {targetGuest.name}
          </Link>
          <div className="text-sm text-gray-600">
            {targetGuest.title && <span>{targetGuest.title}</span>}
            {targetGuest.title && targetGuest.company && <span> at </span>}
            {targetGuest.company && <span>{targetGuest.company}</span>}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {targetGuest.location?.name && (
              <span className="flex items-center gap-1">
                <span>{targetGuest.location.name}</span>
              </span>
            )}
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={statusClass}>
            {targetGuest.status}
          </Badge>
          
          <Badge variant="outline" className="border-gray-300">
            {priorityLabel} Priority
          </Badge>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onConvertClick(targetGuest.id || '')}
            icon={<UserCheck size={16} />}
            className="ml-2"
          >
            Convert
          </Button>
        </div>
      </div>
    </div>
  );
});

TargetGuestRow.displayName = 'TargetGuestRow';

/**
 * Enhanced TargetGuestList Component
 * 
 * @returns React component
 */
const TargetGuestList: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: '',
    priority: ''
  });
  const [sort, setSort] = useState<SortState>({
    field: 'name',
    direction: 'asc'
  });
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedTargetGuestId, setSelectedTargetGuestId] = useState<string>('');
  
  // Use the enhanced hook with caching
  const { 
    targetGuests, 
    isLoading, 
    error, 
    fetchTargetGuests, 
    convertToGuest,
    retryFetch
  } = useTargetGuests({
    search: filters.search,
    status: filters.status,
    priority: filters.priority
  });
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };
  
  // Apply search filter
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, search: searchInput }));
  };
  
  // Clear search filter
  const handleClearSearch = () => {
    setSearchInput('');
    setFilters(prev => ({ ...prev, search: '' }));
  };
  
  // Handle status filter change
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters(prev => ({ ...prev, status: e.target.value }));
  };
  
  // Handle priority filter change
  const handlePriorityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters(prev => ({ ...prev, priority: e.target.value }));
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: ''
    });
    setSearchInput('');
  };
  
  // Handle sort change
  const handleSortChange = (field: keyof TargetGuest) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Handle convert to guest button click
  const handleConvertClick = (id: string) => {
    setSelectedTargetGuestId(id);
    setConvertDialogOpen(true);
  };
  
   // Handle convert to guest confirmation
  const handleConvertConfirm = async (additionalData: Record<string, unknown>) => {
    if (!selectedTargetGuestId) return;
    
    const result = await convertToGuest(selectedTargetGuestId, additionalData);
    
    if (result.data) {
      // Refresh the list after successful conversion
      fetchTargetGuests();
    }
    
    setConvertDialogOpen(false);
  };
  
  // Filter and sort target guests
  const filteredAndSortedTargetGuests = useMemo(() => {
    if (!targetGuests?.data) return [];
    
    // Create a copy of the data to avoid mutating the original
    const data = [...targetGuests.data];
    
    // Sort the data
    if (sort.field) {
      data.sort((a, b) => {
        // Type-safe property access with field validation
        if (sort.field === '') return 0;
        
        // Safely access properties with type checking
        const aValue = sort.field in a ? a[sort.field] : null;
        const bValue = sort.field in b ? b[sort.field] : null;
        
        // Handle undefined or null values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sort.direction === 'asc' ? -1 : 1;
        if (bValue == null) return sort.direction === 'asc' ? 1 : -1;
        
        // Compare values based on type
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sort.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Default comparison for other types
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sort.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }
        
        // Fallback for other types
        return 0;
      });
    }
    
    return data;
  }, [targetGuests?.data, sort.field, sort.direction]);
  
  // Render sort indicator
  const renderSortIndicator = (field: keyof TargetGuest) => {
    if (sort.field !== field) return null;
    
    return sort.direction === 'asc' 
      ? <ChevronUp className="h-4 w-4" /> 
      : <ChevronDown className="h-4 w-4" />;
  };
  
  // Render row for virtualized list
  const renderRow = useCallback(({ index, style }: { index: number, style: React.CSSProperties }) => {
    const targetGuest = filteredAndSortedTargetGuests[index];
    if (!targetGuest) return null;
    
    return (
      <TargetGuestRow 
        targetGuest={targetGuest} 
        style={style} 
        onConvertClick={handleConvertClick} 
      />
    );
  }, [filteredAndSortedTargetGuests]);
  
  // Use performance measurement hook - using import.meta.env.DEV properly cast to boolean
  usePerformanceMeasure.usePerformanceMeasure({
    componentName: 'TargetGuestList',
    logToConsole: !!import.meta.env.DEV
  });
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Target Guests</h1>
        <Button 
          onClick={() => navigate('/target-guests/new')}
          icon={<UserPlus size={16} />}
        >
          Add Target Guest
        </Button>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <p className="font-medium">Error loading target guests</p>
          <p className="text-sm">{error.message}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={retryFetch}
            className="mt-2"
            icon={<RefreshCw size={16} />}
          >
            Retry
          </Button>
        </div>
      )}
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">Target Guest List</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search and filter controls */}
          <div className="mb-4 space-y-4">
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search target guests..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                {searchInput && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              <Button type="submit" variant="default">Search</Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                icon={<Filter size={16} />}
              >
                Filters
              </Button>
              {(filters.status || filters.priority) && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={handleClearFilters}
                  icon={<X size={16} />}
                >
                  Clear
                </Button>
              )}
            </form>
            
            {/* Additional filters */}
            {showFilters && (
              <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-md">
              <div className="w-40">
                <select
                  id="status"
                  value={filters.status}
                  onChange={handleStatusChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Filter by status"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="interested">Interested</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="converted">Converted</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <div className="w-40">
                <select
                  id="priority"
                  value={filters.priority}
                  onChange={handlePriorityChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Filter by priority"
                >
                  <option value="">All Priority</option>
                  <option value="1">High</option>
                  <option value="3">Medium</option>
                  <option value="5">Low</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClearFilters}
                  icon={<X size={16} />}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
            )}
          </div>
          
          {/* Sort controls */}
          <div className="flex border-b border-gray-200 pb-2 mb-2">
            <button
              onClick={() => handleSortChange('name')}
              className={`flex items-center px-4 py-2 text-sm font-medium ${
                sort.field === 'name' ? 'text-primary-600' : 'text-gray-600'
              }`}
            >
              Name {renderSortIndicator('name')}
            </button>
            <button
              onClick={() => handleSortChange('status')}
              className={`flex items-center px-4 py-2 text-sm font-medium ${
                sort.field === 'status' ? 'text-primary-600' : 'text-gray-600'
              }`}
            >
              Status {renderSortIndicator('status')}
            </button>
            <button
              onClick={() => handleSortChange('priority')}
              className={`flex items-center px-4 py-2 text-sm font-medium ${
                sort.field === 'priority' ? 'text-primary-600' : 'text-gray-600'
              }`}
            >
              Priority {renderSortIndicator('priority')}
            </button>
          </div>
          
          {/* Target guest list */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
              <span className="ml-3">Loading target guests...</span>
            </div>
          ) : filteredAndSortedTargetGuests.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No target guests found</p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/target-guests/new')} 
                className="mt-4"
                icon={<UserPlus size={16} />}
              >
                Add Target Guest
              </Button>
            </div>
          ) : (
            <div className="h-[600px] w-full">
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    width={width}
                    itemCount={filteredAndSortedTargetGuests.length}
                    itemSize={100}
                  >
                    {renderRow}
                  </List>
                )}
              </AutoSizer>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Convert to guest dialog */}
      {convertDialogOpen && selectedTargetGuestId && (
        <ConvertToGuest
          targetGuestId={selectedTargetGuestId}
          open={convertDialogOpen}
          onClose={() => setConvertDialogOpen(false)}
          onConfirm={handleConvertConfirm}
        />
      )}
    </div>
  );
};

export default TargetGuestList;
